import React, { useState } from 'react';
import Box from '@mui/system/Box';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import { IconButton, Link, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';

import {
  CorporateFare as FederationIcon,
  BorderColor as PencilIcon,
  CalendarToday as CalendarIcon,
  Groups as TeamIcon,
  SportsSoccer as FootballIcon,
  Layers as CategoryIcon,
  Business as HqIcon,
  Factory as IndustryIcon,
  Person as AthleteIcon,
  School as SchoolIcon,
  Sports as PartnerIcon,
  Storefront as BrandIcon,
} from '@mui/icons-material';

import SidebarInfoBlock from '@/components/shared/Sidebar/SidebarInfoBlock';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Tooltip from '@mui/material/Tooltip';
import SvgIcon from '@mui/material/SvgIcon';
import Skeleton from '@mui/material/Skeleton'; // eslint-disable-line import/no-unresolved
import BackToPlayBookBtn from '@/pages/brandprofile/BackToPlayBookBtn';

const HeaderLoader: React.FC = () => (
  <Box className='loader' sx={{ width: '100%' }}>
    <Skeleton
      width={'100%'}
      height={100}
      sx={{ transform: 'none', mb: '8px', backgroundColor: 'tertiary.dark' }}
    />
    <Skeleton width={'100%'} height={30} sx={{ backgroundColor: 'tertiary.dark' }} />
    <Skeleton width={'100%'} height={30} sx={{ backgroundColor: 'tertiary.dark' }} />
  </Box>
);

const SecondarySidebar: React.FC = () => {
  const [showCameraIcon, setShowCameraIcon] = useState(false);
  const { secondarySidebarData, showSettingsSidebar } = useSidebarNavigation();
  const { title, editButton, img, industry, headquarter, category, current_club, foundation_date } =
    secondarySidebarData.info;

  const normalize = (value: string | undefined | null) => String(value || '').toLowerCase();

  const hasUsableImage = Boolean(
    img &&
      !normalize(img).includes('imageplaceholder') &&
      !normalize(img).includes('avatar.png') &&
      normalize(img) !== 'no image',
  );

  const getFallbackType = () => {
    const categoryText = normalize(category);
    const titleText = normalize(title);
    const currentClubText = normalize(current_club);
    const hasBrandMeta = Boolean(industry || headquarter);

    if (hasBrandMeta) {
      return 'brand';
    }
    if (
      currentClubText ||
      categoryText.includes('athlete') ||
      titleText.includes('athlete') ||
      titleText.includes('player')
    ) {
      return 'athlete';
    }
    if (
      categoryText.includes('federation') ||
      categoryText.includes('association') ||
      categoryText.includes('league') ||
      categoryText.includes('union')
    ) {
      return 'federation';
    }
    if (
      categoryText.includes('school') ||
      categoryText.includes('academy') ||
      categoryText.includes('college') ||
      categoryText.includes('university')
    ) {
      return 'school';
    }
    if (
      categoryText.includes('team') ||
      categoryText.includes('club') ||
      titleText.includes('fc') ||
      titleText.includes('club')
    ) {
      return 'team';
    }
    return 'partner';
  };

  const fallbackType = getFallbackType();
  const fallbackIconMap: Record<string, React.ElementType> = {
    brand: BrandIcon,
    athlete: AthleteIcon,
    team: TeamIcon,
    federation: FederationIcon,
    school: SchoolIcon,
    partner: PartnerIcon,
  };
  const fallbackBgMap: Record<string, string> = {
    brand: 'linear-gradient(145deg, #3a4f7a 0%, #1f2f51 100%)',
    athlete: 'linear-gradient(145deg, #3f5d8f 0%, #243b62 100%)',
    team: 'linear-gradient(145deg, #48638f 0%, #2e4468 100%)',
    federation: 'linear-gradient(145deg, #48638f 0%, #2e4468 100%)',
    school: 'linear-gradient(145deg, #48638f 0%, #2e4468 100%)',
    partner: 'linear-gradient(145deg, #48638f 0%, #2e4468 100%)',
  };
  const FallbackIcon = fallbackIconMap[fallbackType] || PartnerIcon;

  const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // updateValue
    secondarySidebarData.updateBrandImg && secondarySidebarData.updateBrandImg(event);
  };

  return (
    <Box
      className='secondaryNav'
      sx={{
        width: '150px',
        height: 'calc(100vh - 50px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'primary.main',
        display: 'flex',
        flexDirection: 'column',
        padding: '8px',
        gap: '20px',
        mt: '50px',
        pointerEvents: 'auto',
        position: 'relative',
        zIndex: 1,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
      }}
    >
      {showSettingsSidebar ? null : (
        <>
          {/* Header. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Box
              onMouseOver={() => setShowCameraIcon(true)}
              onMouseLeave={() => setShowCameraIcon(false)}
              sx={{
                width: '100%',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '130px',
              }}
            >
              {title ? (
                <>
                  {hasUsableImage ? (
                    <img
                      src={img}
                      alt='Profile picture'
                      style={{
                        margin: '0 auto',
                        borderRadius: '8px',
                        height: 'auto',
                        width: '100%',
                        opacity: showCameraIcon ? '0.7' : '1',
                        transition: 'opacity .3s',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(255,255,255,0.35)',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: '130px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.35)',
                        background: fallbackBgMap[fallbackType] || fallbackBgMap.partner,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: showCameraIcon ? '0.8' : '1',
                        transition: 'opacity .3s',
                      }}
                    >
                      <SvgIcon component={FallbackIcon} sx={{ color: '#fff', fontSize: '56px' }} />
                    </Box>
                  )}
                  <input
                    style={{ display: 'none' }}
                    accept='image/*'
                    onChange={onImageChange}
                    id='icon-button-file'
                    type='file'
                  />
                  {showCameraIcon && (
                    <Box className='changeprofile_image'>
                      <Tooltip title='Change the image'>
                        <label htmlFor='icon-button-file'>
                          <IconButton
                            color='primary'
                            aria-label='upload picture'
                            component='span'
                            size='large'
                          >
                            <PhotoCamera />
                          </IconButton>
                        </label>
                      </Tooltip>
                    </Box>
                  )}
                </>
              ) : (
                <HeaderLoader />
              )}
            </Box>

            {title && (
              <>
                <Typography
                  sx={{
                    color: 'typography.main',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                  }}
                >
                  {title}
                </Typography>
                <Link
                  id={editButton.id}
                  key={editButton.id + editButton.label}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover, &:focus': {
                      textDecoration: 'none',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      boxShadow: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '1',
                      padding: '10px 10px 5px',
                      borderRadius: '40px',
                      transition: 'color .5s, background-color .5s',
                      color: 'typography.main',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      '&:hover, &:focus': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <SvgIcon component={PencilIcon} sx={{ height: '15px' }} />
                    View profile
                  </Typography>
                </Link>
                <Divider sx={{ borderColor: 'typography.main' }} />
              </>
            )}

            {industry && (
              <SidebarInfoBlock tooltip='Industry' value={industry} icon={IndustryIcon} />
            )}
            {headquarter && (
              <SidebarInfoBlock tooltip='Headquarter' value={headquarter} icon={HqIcon} />
            )}
            {category && (
              <SidebarInfoBlock tooltip='Category' value={category} icon={CategoryIcon} />
            )}
            {current_club && (
              <SidebarInfoBlock tooltip='Current club' value={current_club} icon={FootballIcon} />
            )}
            {foundation_date && (
              <SidebarInfoBlock
                tooltip='Foundation date'
                value={foundation_date}
                icon={CalendarIcon}
              />
            )}

            {hasUsableImage ||
            title ||
            industry ||
            headquarter ||
            category ||
            current_club ||
            foundation_date ? (
              <Divider sx={{ borderColor: 'typography.main', zIndex: '0' }} />
            ) : null}
          </Box>

          <BackToPlayBookBtn />

          {/* Navigation. */}
          {secondarySidebarData.links && (
            <Box
              sx={{
                margin: '0 -8px',
                paddingLeft: '7px',
              }}
            >
              {secondarySidebarData.links.map(({ label, id, isActive }, index) => (
                <Link
                  id={id}
                  key={id + index}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    position: 'relative',
                    '&:hover, &:focus': {
                      textDecoration: 'none',
                    },
                    ...(isActive && {
                      '&:before, &:after': {
                        content: '""',
                        backgroundColor: 'tertiary.dark',
                        width: '10px',
                        height: '10px',
                        display: 'block',
                        position: 'absolute',
                        right: '0',
                      },
                      '&:before': {
                        top: '-10px',
                        zIndex: '0',
                      },
                      '&:after': {
                        bottom: '-10px',
                      },
                    }),
                  }}
                >
                  <Typography
                    sx={{
                      boxShadow: 'none',
                      fontSize: '12px',
                      fontWeight: '500',
                      padding: '10px',
                      borderRadius: '40px',
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      transition: 'color .5s, padding-left .3s',
                      color: isActive ? 'primary.main' : 'tertiary.dark',
                      backgroundColor: isActive ? 'tertiary.dark' : 'transparent',
                      textDecoration: 'none',
                      '&:hover, &:focus': {
                        color: isActive ? 'primary.main' : 'tertiary.dark',
                        textDecoration: 'none',
                        paddingLeft: '3px',
                      },
                      ...(isActive && {
                        '&:before, &:after': {
                          content: '""',
                          backgroundColor: 'primary.main',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'block',
                          position: 'absolute',
                          right: '0',
                        },
                        '&:before': {
                          top: '-20px',
                        },
                        '&:after': {
                          bottom: '-20px',
                          zIndex: '1',
                        },
                      }),
                    }}
                    key={id}
                    id={id}
                  >
                    {label}
                  </Typography>
                </Link>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SecondarySidebar;
